import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { inr, formatDate, statusColor } from "@/lib/format";

export default function TenantSellers() {
  return (
    <div className="p-8">
      <h1 className="font-display text-3xl font-bold tracking-tight mb-1" data-testid="tenant-sellers-title">Sellers, Settlements & Staff</h1>
      <p className="text-slate-500 text-sm mb-6">Marketplace partners and tenant team management.</p>
      <Tabs defaultValue="sellers">
        <TabsList>
          <TabsTrigger value="sellers" data-testid="tab-sellers">Sellers</TabsTrigger>
          <TabsTrigger value="settlements" data-testid="tab-settlements">Settlements</TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-staff">Staff</TabsTrigger>
        </TabsList>
        <TabsContent value="sellers"><SellersTab /></TabsContent>
        <TabsContent value="settlements"><SettlementsTab /></TabsContent>
        <TabsContent value="staff"><StaffTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function SellersTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", business_name: "", email: "", address: "", commission_type: "percent", commission_value: 10 });
  const refresh = () => api.get("/tenant/sellers").then(({ data }) => setItems(data));
  useEffect(() => { refresh(); }, []);
  const save = async () => {
    try {
      await api.post("/tenant/sellers", form);
      toast.success("Seller added");
      setOpen(false);
      setForm({ name: "", phone: "", business_name: "", email: "", address: "", commission_type: "percent", commission_value: 10 });
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (confirm("Remove seller?")) { await api.delete(`/tenant/sellers/${id}`); refresh(); } };
  return (
    <Card className="p-5 mt-4 border-slate-200">
      <div className="flex justify-end mb-4">
        <Button onClick={() => setOpen(true)} data-testid="seller-create-btn"><Plus className="h-4 w-4 mr-2" />Add Seller</Button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr><th className="text-left px-3 py-2">Seller</th><th className="text-left px-3 py-2">Phone</th><th className="text-left px-3 py-2">Commission</th><th></th></tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.id} className="border-t border-slate-100" data-testid={`seller-row-${s.id}`}>
              <td className="px-3 py-2"><div className="font-medium">{s.name}</div><div className="text-xs text-slate-500">{s.business_name}</div></td>
              <td className="px-3 py-2 font-mono text-xs">{s.phone}</td>
              <td className="px-3 py-2 text-xs">
                {s.commission_value != null ? (
                  <>{s.commission_type === "percent" ? `${s.commission_value}%` : inr(s.commission_value)}</>
                ) : <span className="text-slate-400">Default</span>}
              </td>
              <td className="px-3 py-2 text-right"><Button size="icon" variant="ghost" onClick={() => del(s.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button></td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={4} className="text-center text-slate-400 py-6">No sellers yet</td></tr>}
        </tbody>
      </table>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Seller</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="seller-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Phone</Label><Input data-testid="seller-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Business name</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Commission type</Label>
                <Select value={form.commission_type} onValueChange={(v) => setForm({ ...form, commission_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percent">Percent</SelectItem><SelectItem value="flat">Flat ₹</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Value</Label><Input type="number" value={form.commission_value} onChange={(e) => setForm({ ...form, commission_value: +e.target.value })} /></div>
            </div>
            <Button className="w-full" onClick={save} data-testid="seller-save-btn">Save Seller</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function SettlementsTab() {
  const [items, setItems] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ seller_id: "", period_from: "", period_to: "" });
  const refresh = () => {
    api.get("/tenant/settlements").then(({ data }) => setItems(data));
    api.get("/tenant/sellers").then(({ data }) => setSellers(data));
  };
  useEffect(() => { refresh(); }, []);
  const save = async () => {
    try {
      await api.post("/tenant/settlements", form);
      toast.success("Settlement generated");
      setOpen(false);
      setForm({ seller_id: "", period_from: "", period_to: "" });
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const pay = async (id) => {
    if (!confirm("Mark as paid (mock payout)?")) return;
    await api.post(`/tenant/settlements/${id}/pay`);
    refresh();
  };
  return (
    <Card className="p-5 mt-4 border-slate-200">
      <div className="flex justify-end mb-4"><Button onClick={() => setOpen(true)} data-testid="settlement-create-btn"><Plus className="h-4 w-4 mr-2" />Generate Settlement</Button></div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr><th className="text-left px-3 py-2">Seller</th><th className="text-left px-3 py-2">Period</th><th className="text-left px-3 py-2">Gross</th><th className="text-left px-3 py-2">Commission</th><th className="text-left px-3 py-2">Payout</th><th className="text-left px-3 py-2">Status</th><th></th></tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.id} className="border-t border-slate-100" data-testid={`settlement-row-${s.id}`}>
              <td className="px-3 py-2"><div className="font-medium">{s.seller?.name}</div><div className="text-xs text-slate-500">{s.seller?.business_name}</div></td>
              <td className="px-3 py-2 text-xs">{formatDate(s.period_from)} → {formatDate(s.period_to)}</td>
              <td className="px-3 py-2">{inr(s.gross_amount)}</td>
              <td className="px-3 py-2">{inr(s.commission_amount)}</td>
              <td className="px-3 py-2 font-semibold">{inr(s.net_payout)}</td>
              <td className="px-3 py-2"><span className={`pill ${statusColor(s.status)}`}>{s.status}</span></td>
              <td className="px-3 py-2 text-right">{s.status !== "paid" && <Button size="sm" onClick={() => pay(s.id)} data-testid={`settlement-pay-${s.id}`}>Pay</Button>}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-6">No settlements</td></tr>}
        </tbody>
      </table>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Generate Settlement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Seller</Label>
              <Select value={form.seller_id} onValueChange={(v) => setForm({ ...form, seller_id: v })}>
                <SelectTrigger data-testid="settlement-seller-select"><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent>{sellers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} – {s.business_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>From date</Label><Input type="date" value={form.period_from} onChange={(e) => setForm({ ...form, period_from: e.target.value })} /></div>
            <div><Label>To date</Label><Input type="date" value={form.period_to} onChange={(e) => setForm({ ...form, period_to: e.target.value })} /></div>
            <Button className="w-full" onClick={save} data-testid="settlement-save-btn">Generate</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function StaffTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", role: "tenant_staff", email: "" });
  const refresh = () => api.get("/tenant/staff").then(({ data }) => setItems(data));
  useEffect(() => { refresh(); }, []);
  const save = async () => {
    try {
      await api.post("/tenant/staff", form);
      toast.success("Staff added");
      setOpen(false);
      setForm({ name: "", phone: "", role: "tenant_staff", email: "" });
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (confirm("Remove staff?")) { await api.delete(`/tenant/staff/${id}`); refresh(); } };
  return (
    <Card className="p-5 mt-4 border-slate-200">
      <div className="flex justify-end mb-4"><Button onClick={() => setOpen(true)} data-testid="staff-create-btn"><Plus className="h-4 w-4 mr-2" />Add Staff</Button></div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Phone</th><th className="text-left px-3 py-2">Role</th><th></th></tr>
        </thead>
        <tbody>
          {items.map((u) => (
            <tr key={u.id} className="border-t border-slate-100" data-testid={`staff-row-${u.id}`}>
              <td className="px-3 py-2 font-medium">{u.name}</td>
              <td className="px-3 py-2 font-mono text-xs">{u.phone}</td>
              <td className="px-3 py-2"><span className="pill bg-slate-100 text-slate-700">{u.role?.replace("_", " ")}</span></td>
              <td className="px-3 py-2 text-right"><Button size="icon" variant="ghost" onClick={() => del(u.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button></td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={4} className="text-center text-slate-400 py-6">No staff</td></tr>}
        </tbody>
      </table>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Staff</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="staff-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Phone</Label><Input data-testid="staff-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tenant_staff">Admin Staff</SelectItem>
                  <SelectItem value="sales_staff">Sales Staff</SelectItem>
                  <SelectItem value="service_tech">Service Technician</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full" onClick={save} data-testid="staff-save-btn">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
